import { useState, useMemo } from "react";
import { useUsers } from "@/hooks/useUsers";
import { useCongridUsers } from "@/hooks/useCongridUsers";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SearchIcon, UsersIcon, EditIcon } from "@/lib/icons";
import { UserEditDialog } from "@/components/users/UserEditDialog";
import type { User, UserAccessLevel } from "@/types";

function AccessLevelBadge({ level }: { level: UserAccessLevel }) {
  if (level === 'ADMIN') {
    return <Badge variant="destructive">Ylläpitäjä</Badge>;
  }
  if (level === 'INTERNAL_PLUS') {
    return <Badge variant="default" className="bg-indigo-600 hover:bg-indigo-600">Sisäinen+</Badge>;
  }
  if (level === 'INTERNAL') {
    return <Badge variant="default" className="bg-blue-600 hover:bg-blue-600">Sisäinen</Badge>;
  }
  return <Badge variant="secondary">Vieras</Badge>;
}

export function Kayttajat() {
  const [search, setSearch] = useState("");
  const { users, loading } = useUsers();
  const { dbUser: currentDbUser } = useAuth();
  // Pre-fetch Congrid users so Apollo cache is warm when dialog opens
  useCongridUsers();
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Mirrors the backend access-level transition matrix (see UserEditDialog):
  // ADMIN may edit anyone; INTERNAL_PLUS/INTERNAL may only edit targets that
  // are currently GUEST or INTERNAL (never ADMIN or INTERNAL_PLUS targets,
  // since only ADMIN can grant/revoke INTERNAL_PLUS). GUEST can't edit anyone.
  const canEditUser = (targetUser: User): boolean => {
    if (!currentDbUser) return false;
    if (currentDbUser.accessLevel === 'ADMIN') return true;
    if (currentDbUser.accessLevel === 'INTERNAL_PLUS' || currentDbUser.accessLevel === 'INTERNAL') {
      return targetUser.accessLevel === 'GUEST' || targetUser.accessLevel === 'INTERNAL';
    }
    return false;
  };

  const openEdit = (user: User) => {
    if (!canEditUser(user)) return;
    setEditingUser(user);
    setDialogOpen(true);
  };

  const filteredUsers = useMemo(() => {
    if (!search) return users;
    const lower = search.toLowerCase();
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(lower) ||
        u.firstName?.toLowerCase().includes(lower) ||
        u.lastName?.toLowerCase().includes(lower),
    );
  }, [users, search]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-9 w-32" />
          <Skeleton className="h-5 w-48 mt-2" />
        </div>
        <Skeleton className="h-10 w-64" />
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {["Nimi", "Sähköposti", "Käyttöoikeus", "Tila", "Viimeisin kirjautuminen"].map(
                  (h) => (
                    <TableHead key={h}>{h}</TableHead>
                  ),
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {[1, 2, 3, 4, 5].map((i) => (
                <TableRow key={i}>
                  {[1, 2, 3, 4, 5].map((j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-5 w-24" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Frozen top region: header bar and stats row stay pinned while the
          table scrolls beneath. Sticky relative to AppLayout's scrolling
          <main>; -mt-6 on the wrapper cancels the layout's top padding. */}
      <div className="sticky top-0 z-20 -mt-6 bg-background">
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b bg-background shrink-0 -mx-6">
          <h1 className="text-lg font-semibold mr-2">Käyttäjät</h1>

          {/* Search */}
          <div className="relative flex-1 min-w-[200]px max-w-sm">
            <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Hae käyttäjiä..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="flex pt-3 pb-3 gap-3">
        <div className="flex items-center gap-2 rounded-lg border px-4 py-2">
          <UsersIcon className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">
            {filteredUsers.length} käyttäjää
          </span>
        </div>
        <div className="flex items-center gap-2 rounded-lg border px-4 py-2">
          <div className="h-2 w-2 rounded-full bg-green-500" />
          <span className="text-sm font-medium">
            {filteredUsers.filter((u) => u.isActive).length} aktiivista
          </span>
        </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nimi</TableHead>
              <TableHead>Sähköposti</TableHead>
              <TableHead>Käyttöoikeus</TableHead>
              <TableHead>Congrid</TableHead>
              <TableHead>Tila</TableHead>
              <TableHead>Viimeisin kirjautuminen</TableHead>
              <TableHead>Luotu</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow
                key={user.id}
                className={canEditUser(user) ? "cursor-pointer hover:bg-muted/50" : ""}
                onClick={() => openEdit(user)}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs">
                        {(user.firstName?.charAt(0) ?? "") +
                          (user.lastName?.charAt(0) ?? "")}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{[user.firstName, user.lastName].filter(Boolean).join(" ") || user.email}</p>
                      {user.firstName && user.lastName && (
                        <p className="text-xs text-muted-foreground">
                          {user.firstName} {user.lastName}
                        </p>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {user.email}
                </TableCell>
                <TableCell>
                  <AccessLevelBadge level={user.accessLevel} />
                </TableCell>
                <TableCell>
                  {user.congridUserId ? (
                    <Badge
                      variant="secondary"
                      className="bg-blue-100 text-blue-700"
                    >
                      Linkitetty
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">–</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="secondary"
                    className={
                      user.isActive
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-500"
                    }
                  >
                    {user.isActive ? "Aktiivinen" : "Ei aktiivinen"}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {user.lastLoginAt
                    ? new Date(user.lastLoginAt).toLocaleDateString("fi", {
                        day: "numeric",
                        month: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "–"}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {new Date(user.createdAt).toLocaleDateString("fi")}
                </TableCell>
                <TableCell className="text-right">
                  {canEditUser(user) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); openEdit(user); }}
                      className="h-7 w-7"
                    >
                      <EditIcon className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filteredUsers.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center text-muted-foreground py-8"
                >
                  Ei käyttäjiä
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <UserEditDialog
        user={editingUser}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </div>
  );
}
